import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { loginWithFingerprint } from "../lib/webauthn";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Ref plutôt qu'une dépendance directe dans fetchMe : évite de recréer
  // fetchMe (et donc de redéclencher les effets qui en dépendent) à chaque
  // changement de `user`, tout en gardant accès à sa valeur la plus récente
  // au moment de l'appel.
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Ne déconnecte réellement que sur un vrai refus d'authentification
      // (401/403) — une coupure réseau passagère lors d'un rafraîchissement
      // en arrière-plan (voir plus bas) ne doit jamais faire perdre la
      // session affichée pour une raison purement transitoire.
      const status = err?.response?.status;
      if (status === 401 || status === 403 || userRef.current === null) setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Bug corrigé : les droits d'un utilisateur n'étaient relus qu'au tout
  // premier chargement de la page — si un administrateur modifiait ses
  // permissions (ex. retirer l'accès au registre fiscal) pendant que son
  // onglet restait ouvert, l'ancienne version restait affichée en mémoire
  // indéfiniment, jusqu'à un rechargement complet ou une reconnexion
  // explicite. On rafraîchit désormais automatiquement dès que l'onglet
  // redevient actif (l'utilisateur revient dessus après être allé
  // ailleurs) — le cas le plus courant où des droits changés côté admin
  // doivent réellement prendre effet sans action explicite de la personne.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchMe();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchMe]);

  // Filet de sécurité complémentaire : si l'onglet reste actif en continu
  // (jamais mis en arrière-plan, donc jamais de "visibilitychange"), un
  // changement de droits par un administrateur doit quand même finir par
  // prendre effet dans un délai raisonnable plutôt que de rester figé pour
  // toute la durée de la session.
  useEffect(() => {
    const interval = setInterval(fetchMe, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchMe]);

  const login = async (matricule, pin) => {
    const { data } = await api.post("/auth/login", { matricule, pin });
    setUser(data.user);
    return data.user;
  };

  const loginBadge = async (badge_code) => {
    const { data } = await api.post("/auth/login-badge", { badge_code });
    setUser(data.user);
    return data.user;
  };

  // Bug corrigé : la connexion par empreinte (WebAuthn) mettait bien à jour
  // le cookie de session côté serveur, mais n'appelait jamais `setUser(...)`
  // — l'application ne savait donc jamais qu'elle était authentifiée et
  // renvoyait vers l'écran de connexion malgré une empreinte pourtant
  // valide. `loginWithFingerprint` (lib/webauthn.js) gère uniquement la
  // partie WebAuthn (défi, signature) et renvoie l'utilisateur — c'est ici,
  // comme pour les deux méthodes ci-dessus, que l'état de connexion de
  // l'application est réellement mis à jour.
  const loginFingerprint = async (matricule) => {
    const result = await loginWithFingerprint(matricule);
    setUser(result.user);
    return result.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  const switchShop = async (shopId) => {
    await api.patch("/users/me/active-shop", { shop_id: shopId });
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginBadge, loginFingerprint, logout, refresh: fetchMe, switchShop }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function hasPerm(user, module, action) {
  if (!user) return false;
  if (user.is_admin) return true;
  const perms = user.permissions || {};
  const mod = perms[module];
  if (typeof mod === "boolean") return mod;
  return !!(mod && mod[action]);
}
